import { trackABConversion } from "@/hooks/useABTesting";
import { logger } from "./logger";

/**
 * Utility to scroll to an element by ID, accounting for sticky headers
 * and specific page layouts.
 */

// Mapping of offsets per route slug to ensure precise scrolling
// Values represent the total sticky height (navbar + secondary navs)
const ROUTE_OFFSETS: Record<string, number> = {
  'erp-tienda-taller-patinetes': 140, // Navbar (64) + secondary sticky (76)
  'erp-scooter-shop-workshop': 140,
  'modulo': 120,
  'solucion': 120,
  'home': 96,
  'default': 96
};

/**
 * Determines the best offset for the current page based on route and device
 */
const getPageOffset = () => {
  const pathname = window.location.pathname;
  const segments = pathname.split('/').filter(Boolean);
  const isMobile = typeof window !== 'undefined' ? window.innerWidth < 768 : false;
  
  // Skip language prefix if present
  const mainSegment = segments.length > 0 && segments[0].length === 2 ? segments[1] : segments[0];
  
  const baseOffset = !mainSegment ? ROUTE_OFFSETS['home'] : (ROUTE_OFFSETS[mainSegment] || ROUTE_OFFSETS['default']);
  
  // Reduce offset slightly on mobile if headers are smaller or hidden
  return isMobile ? Math.max(64, baseOffset - 20) : baseOffset;
};

/**
 * Core scroll function with telemetry and focus management
 */
export const scrollToElement = (elementId: string, smooth = true, ctaId?: string) => {
  const startTime = performance.now();
  const cleanId = elementId.replace("#", "");
  let element = document.getElementById(cleanId);
  
  // 1. Pre-check and force render if missing
  if (!element) {
    window.dispatchEvent(new CustomEvent('force-render-section', { detail: { elementId: cleanId } }));
    
    trackABConversion('scroll_attempt', 'element_missing', { 
      target: cleanId, 
      cta_id: ctaId,
      status: 'failure',
      reason: 'element_not_found_in_dom'
    });
    
    logger.debug(`Element #${cleanId} not found, requesting force-render`, { ctaId });
    return false;
  }

  // 2. Calculate dynamic offset
  const header = document.querySelector("nav");
  const isRTL = document.documentElement.dir === 'rtl';
  const headerHeight = header ? (header as HTMLElement).offsetHeight : 64;
  const pageMappingOffset = getPageOffset();
  
  // Use the maximum of either the mapping or the actual header height + buffer
  const offset = Math.max(pageMappingOffset, headerHeight + 20);
  
  const elementPosition = element.getBoundingClientRect().top;
  const offsetPosition = elementPosition + window.pageYOffset - offset;

  try {
    window.scrollTo({
      top: offsetPosition,
      behavior: smooth ? "smooth" : "auto",
    });

    const duration = performance.now() - startTime;
    
    // 3. Structured Telemetry
    trackABConversion('scroll_event', 'scroll_success', {
      target: cleanId,
      cta_id: ctaId,
      offset: Math.round(offset),
      duration_ms: Math.round(duration),
      device: window.innerWidth < 768 ? 'mobile' : 'desktop',
      status: 'success'
    });

    // 4. Accessibility and UX
    element.setAttribute("tabindex", "-1");
    element.focus({ preventScroll: true });
    
    const announcementId = 'scroll-announcement';
    let announcement = document.getElementById(announcementId);
    if (!announcement) {
      announcement = document.createElement("div");
      announcement.id = announcementId;
      announcement.setAttribute("aria-live", "assertive");
      announcement.className = "sr-only";
      document.body.appendChild(announcement);
    }
    
    const sectionName = element.querySelector('h1, h2, h3')?.textContent || cleanId;
    announcement.textContent = isRTL ? `التنقل إلى قسم ${sectionName}` : `Navegando a la sección ${sectionName}`;
    
    setTimeout(() => {
      if (announcement) announcement.textContent = "";
    }, 3000);
    
    return true;
  } catch (error) {
    const failureReason = error instanceof Error ? error.message : 'unknown_error';
    
    trackABConversion('scroll_event', 'scroll_error', {
      target: cleanId,
      cta_id: ctaId,
      status: 'error',
      reason: failureReason
    });
    
    logger.error(`Scroll error to #${cleanId}`, { error, ctaId });
    return false;
  }
};
